import { RefCallback, RefObject, useCallback, useLayoutEffect, useState } from "react";

export interface IUseScrolledIntoViewConfiguration<TWrapperElement extends Element = HTMLElement> {
	rootMargin?: IntersectionObserverInit["rootMargin"];
	threshold?: IntersectionObserverInit["threshold"];
	wrapperContainerRef: RefObject<TWrapperElement>;
}

export const useSimpleIntersection = <TWrapperElement extends Element = HTMLElement, TObservedElement extends Element = HTMLElement>({
	rootMargin = "0px",
	threshold = 1,
	wrapperContainerRef,
}: Readonly<IUseScrolledIntoViewConfiguration<TWrapperElement>>) => {
	const [observedElementRef, setObservedElementRef] = useState<TObservedElement | null>(null);

	const [isIntersecting, setIsIntersecting] = useState(false);

	const wrapperContainerCallbackRef: RefCallback<TWrapperElement> = useCallback((ref) => {
		if (!ref) {
			return;
		}
		setIsIntersecting(true);
	}, []);

	const observedElementCallbackRef: RefCallback<TObservedElement> = useCallback((ref) => {
		setIsIntersecting(true);
		setObservedElementRef(ref);
	}, []);

	useLayoutEffect(() => {
		if (!observedElementRef || !wrapperContainerRef) {
			return () => {};
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsIntersecting(entry.isIntersecting);
			},
			{
				root: wrapperContainerRef?.current,
				rootMargin,
				threshold,
			}
		);
		observer.observe(observedElementRef);

		return () => {
			observer.disconnect();
		};
	}, [rootMargin, threshold, observedElementRef, wrapperContainerRef]);

	return {
		wrapperContainerRef: wrapperContainerCallbackRef,
		observedElementRef: observedElementCallbackRef,
		isIntersecting,
	};
};
